import {AlertProps, DataStatus, IconButton, Parameter, Tooltip} from 'platform/components';
import {CSSDimension, Integer, Show, ThemeIconKey} from 'platform/foundation';

import {ReactNode} from 'react';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EntityHeaderControl} from '../EntityHeader/components/EntityHeaderControls';
import {SnippetProps} from '../EntityHeader/components/EntityHeaderSnippet';
import {
  EntityHeader,
  EntityHeaderDropdownFlag,
  EntityHeaderFlag,
} from '../EntityHeader/EntityHeader';
import {EntityHeaderAction} from '../EntityHeader/types/EntityHeaderAction';
import {HeaderPermissions} from '../EntityHeader/types/headerPermissions';
import {VehicleHistoryControlConfig} from '../EntityHeader/types/VehicleHistoryControlConfig';
import {NavigationItem, Page, PageSectionNavigation} from '../Page/Page';
import {SectionItem, Sections} from '../Sections/Sections';
import {ControlledSection} from '../Sections/types/ControlledSection';
import {CompactEntityHeader} from './components/CompactEntityHeader';

export type DetailTemplateHeader = {
  title: string;
  height?: CSSDimension | Integer;
  icon?: ThemeIconKey;
  image?: string;
  parameters?: Parameter[];
  flags?: EntityHeaderFlag[];
  dropdownFlags?: EntityHeaderDropdownFlag[];
  secondaryParameter?: string;
  primaryParameter?: string;
  snippets?: SnippetProps[];
  actions?: EntityHeaderAction[];
  resourceId?: EntityResourceIds;
  recordId?: string;
  onImageClick?: () => void;
  controls?: EntityHeaderControl[];
  alerts?: AlertProps[];
  parkingRecordId?: string;
  authorizationProfileRecordId?: string;
  isAuthorizationProfileEditable?: boolean;
  onAuthorizationProfileChange?: () => void;
  vehicleHistory?: VehicleHistoryControlConfig;
  isEntityLoading?: boolean;
  imageRatio?: `${number}/${number}`;
  enableTeams?: boolean;
};

type NavigationOrSection =
  | {
      sections?: never;
      navigation: NavigationItem[];
    }
  | {
      sections: SectionItem[];
      navigation?: never;
    };

export type DetailTemplateHeaderCollapse = {
  isCollapsed: boolean;
  onCollapsedChange: (isCollapsed: boolean) => void;
};

type DetailTemplateProps = {
  isLoading: boolean;
  isError: boolean;
  header?: DetailTemplateHeader;
  defaultSection?: string;
  permissions?: HeaderPermissions;
  isLocked?: boolean;
  isFullHeight?: boolean;
  activityPanel?: ReactNode;
  /**
   * Offers a Collapse header action that turns the record header into one row and moves the
   * section menu into it. The page owns the state, so it survives a remount of the template.
   */
  headerCollapse?: DetailTemplateHeaderCollapse;
} & NavigationOrSection &
  RequiredTestIdProps &
  ControlledSection;

export function DetailTemplate(props: DetailTemplateProps) {
  const isCompactHeader = props.headerCollapse?.isCollapsed === true;

  const collapseHeaderLabel = i18n.t('general.actions.collapseHeader');

  // A custom action: the header has no icon-button type, and a custom action never moves into
  // the overflow menu.
  const collapseHeaderAction: EntityHeaderAction = {
    type: 'custom',
    component: (
      <Tooltip label={collapseHeaderLabel}>
        <IconButton
          aria-label={collapseHeaderLabel}
          icon="navigation/unfold_less"
          priority="secondary"
          onClick={() => props.headerCollapse?.onCollapsedChange(true)}
          data-testid={suffixTestId('collapseHeader', props)}
        />
      </Tooltip>
    ),
  };

  const headerActions = isNotNil(props.headerCollapse)
    ? [...(props.header?.actions ?? []), collapseHeaderAction]
    : props.header?.actions;

  const renderCompactHeader = (sectionNavigation: PageSectionNavigation) => (
    <CompactEntityHeader
      header={props.header}
      sectionNavigation={sectionNavigation}
      onExpand={() => props.headerCollapse?.onCollapsedChange(false)}
      data-testid={suffixTestId('detail', props)}
    />
  );

  const entityHeader = (
    <EntityHeader
      title={props.header?.title}
      parameters={props.header?.parameters}
      image={props.header?.image}
      icon={props.header?.icon}
      flags={props.header?.flags}
      dropdownFlags={props.header?.dropdownFlags}
      primaryParameter={props.header?.primaryParameter}
      secondaryParameter={props.header?.secondaryParameter}
      snippets={props.header?.snippets}
      actions={headerActions}
      recordId={props.header?.recordId}
      resourceId={props.header?.resourceId}
      onImageClick={props.header?.onImageClick}
      controls={props.header?.controls}
      data-testid={suffixTestId('detail', props)}
      permissions={props.permissions}
      alerts={props.header?.alerts}
      imageRatio={props.header?.imageRatio}
      parkingRecordId={props.header?.parkingRecordId}
      authorizationProfileRecordId={props.header?.authorizationProfileRecordId}
      isAuthorizationProfileEditable={props.header?.isAuthorizationProfileEditable}
      onAuthorizationProfileChange={props.header?.onAuthorizationProfileChange}
      vehicleHistory={props.header?.vehicleHistory}
      isEntityLoading={props.header?.isEntityLoading}
      enableTeams={props.header?.enableTeams}
    />
  );

  return (
    <>
      <DataStatus
        isLoading={!props.header || props.isLoading}
        isError={props.isError}
        minHeight={props.header?.height ?? 61}
      >
        <Show when={isNotNilOrEmpty(props.sections) ? props.sections : null}>
          {(sections) => (
            <Sections
              isFullHeight={props.isFullHeight}
              sections={sections}
              defaultSection={props.defaultSection}
              data-testid={suffixTestId('detail', props)}
              activeSectionId={props.activeSectionId}
              onSectionChange={props.onSectionChange}
              activityPanel={props.activityPanel}
              isLocked={props.isLocked}
              header={entityHeader}
              renderHeader={isCompactHeader ? renderCompactHeader : undefined}
              isNavigationCollapsed={isCompactHeader}
            />
          )}
        </Show>
        <Show when={isNotNilOrEmpty(props.navigation)}>
          <Page
            isFullHeight
            navigation={props.navigation}
            data-testid={suffixTestId('detail', props)}
            isLocked={props.isLocked}
            activityPanel={props.activityPanel}
            header={entityHeader}
            renderHeader={isCompactHeader ? renderCompactHeader : undefined}
            isNavigationCollapsed={isCompactHeader}
          />
        </Show>
      </DataStatus>
    </>
  );
}
